/**
 * Sales Pipeline widget shown near the top of the dashboard.
 *
 * Tabs:
 *  - "All Active" (default) — every non-Dispatched order, oldest dispatch-due first
 *  - "Recent (3d)" — orders created or updated in the last 3 days
 *  - "History" — orders older than 3 days (still non-Dispatched)
 *
 * Previously this widget defaulted to "Recent (3d)" which on a live dataset
 * hid ~17 active orders behind a secondary tab, making the dashboard feel
 * like "active orders not showing up". Users now land on the full active
 * list by default.
 */
import React, { useState } from 'react';
import { ShoppingCart, CheckCircle2, AlertTriangle, ChevronRight } from 'lucide-react';

const STATUS_STYLE = {
  'Not Accepted':  { tag: 'bg-amber-500/15 text-amber-200 border-amber-500/40',   label: 'Pending' },
  'Accepted':      { tag: 'bg-sky-500/15 text-sky-200 border-sky-500/40',         label: 'Planning' },
  'In Production': { tag: 'bg-blue-500/15 text-blue-200 border-blue-500/40',      label: 'In Production' },
  'Packing':       { tag: 'bg-violet-500/15 text-violet-200 border-violet-500/40', label: 'Packing' },
  'Dispatched':    { tag: 'bg-green-500/15 text-green-200 border-green-500/40',   label: 'Completed' },
};

const PRIORITY_STYLE = {
  high:    { border: 'border-red-500/40',   tag: 'bg-red-500/15 text-red-300',   label: 'High' },
  general: { border: 'border-blue-500/30',  tag: 'bg-blue-500/15 text-blue-300',  label: 'General' },
  low:     { border: 'border-zinc-700',     tag: 'bg-zinc-800 text-zinc-400',     label: 'Low' },
};

export default function SalesPipelineWidget({ rows, onOpenFull }) {
  const [tab, setTab] = useState('all'); // 'all' | 'recent' | 'history'

  const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 3);
  const isRecent = (r) => {
    const d = r.date ? new Date(r.date) : null;
    if (!d || isNaN(d)) return true;
    return d >= cutoff;
  };
  const recent = rows.filter(isRecent);
  const historical = rows.filter(r => !isRecent(r));
  const shown = tab === 'all' ? rows : tab === 'recent' ? recent : historical;

  const subtitle = tab === 'all'
    ? `${rows.length} active order${rows.length === 1 ? '' : 's'} · sorted by overdue → priority → dispatch due`
    : tab === 'recent'
    ? `${recent.length} order(s) from last 3 days · ${historical.length} older in History`
    : `History · ${historical.length} older order(s)`;

  return (
    <div className="order-[-2]" data-testid="pipeline-cards-widget-wrapper">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="pipeline-cards-widget">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
              <ShoppingCart size={14} className="text-amber-400" /> Sales Pipeline
            </h3>
            <p className="text-zinc-500 text-[11px] mt-0.5">{subtitle}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="flex bg-zinc-950 border border-zinc-800 rounded-lg p-0.5" role="tablist">
              <TabBtn testid="pipeline-all-tab"     active={tab === 'all'}     onClick={() => setTab('all')}>All Active ({rows.length})</TabBtn>
              <TabBtn testid="pipeline-recent-tab"  active={tab === 'recent'}  onClick={() => setTab('recent')}>Recent (3d)</TabBtn>
              <TabBtn testid="pipeline-history-tab" active={tab === 'history'} onClick={() => setTab('history')}>History</TabBtn>
            </div>
            <button onClick={onOpenFull} data-testid="open-pipeline-btn-top"
              className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-0.5">
              Open Full Pipeline <ChevronRight size={12} />
            </button>
          </div>
        </div>
        {shown.length === 0 ? (
          <div className="text-zinc-500 text-xs flex items-center gap-2">
            <CheckCircle2 size={13} className="text-green-400" />
            {tab === 'all'
              ? 'No active orders — all caught up!'
              : tab === 'recent'
              ? `No new sales orders in the last 3 days. ${historical.length} active orders in History — switch tabs above.`
              : 'No older orders — all caught up!'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="pipeline-cards-grid">
            {shown.slice(0, 12).map(r => <PipelineCard key={r.id} row={r} onOpenFull={onOpenFull} />)}
          </div>
        )}
        {shown.length > 12 && (
          <div className="mt-3 text-center">
            <button onClick={onOpenFull} data-testid="pipeline-see-all-btn"
              className="text-[11px] text-amber-400 hover:text-amber-300">
              + {shown.length - 12} more · Open Full Pipeline →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1 rounded-md text-[11px] font-semibold transition-all ${active ? 'bg-amber-500 text-black' : 'text-zinc-400 hover:text-zinc-200'}`}>
      {children}
    </button>
  );
}

function PipelineCard({ row: r, onOpenFull }) {
  const pstyle = PRIORITY_STYLE[r.priority || 'general'];
  const sStyle = STATUS_STYLE[r.status] || STATUS_STYLE['Not Accepted'];
  return (
    <button onClick={onOpenFull} data-testid={`pipeline-card-${r.id}`}
      className={`text-left bg-zinc-950 rounded-xl overflow-hidden border ${pstyle.border} ${r.is_overdue ? 'ring-1 ring-red-500/40' : ''} hover:border-amber-500/50 transition-all`}>
      <div className={`px-3 py-1 border-b ${sStyle.tag} text-[9px] font-bold uppercase tracking-wider flex items-center justify-between`}>
        <span>{sStyle.label}</span>
        <span className="text-[9px] opacity-70 normal-case tracking-normal">Stage</span>
      </div>
      <div className="p-3.5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="font-mono text-[10px] text-amber-300 font-semibold">{r.voucher_no}</span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded ${pstyle.tag} font-semibold`}>{pstyle.label}</span>
        </div>
        <div className="text-zinc-100 text-xs font-medium truncate">{r.client}</div>
        <div className="flex items-center gap-1.5 mt-1 text-[11px]">
          {r.product_code && <span className="font-mono bg-zinc-800 text-zinc-300 px-1.5 py-0.5 rounded">{r.product_code}</span>}
          <span className="text-zinc-400 truncate">{r.product_name}</span>
        </div>
        <div className="text-zinc-500 text-[10px] mt-0.5">Qty: <span className="text-zinc-300 font-mono">{r.qty} {r.unit}</span></div>
        <div className="flex items-center gap-2 mt-2 text-[10px]">
          <div>
            <div className="text-zinc-600">Prod Due</div>
            <div className="text-zinc-300 font-mono">{r.production_due_date || '—'}</div>
          </div>
          <div>
            <div className="text-zinc-600">Dispatch Due</div>
            <div className={`font-mono ${r.is_overdue ? 'text-red-400 font-semibold' : 'text-zinc-300'}`}>
              {r.dispatch_due_date || '—'}
              {r.is_overdue && <AlertTriangle size={9} className="inline ml-0.5" />}
            </div>
          </div>
        </div>
        {r.production_line && (
          <div className="mt-1.5 text-[9px] text-zinc-500">
            Line: <span className="text-purple-300 font-semibold">{r.production_line}</span>
          </div>
        )}
      </div>
    </button>
  );
}
